import { useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"
import { AlertCircle, X } from "@untitledui/icons"
import type { ApiEstimate, EstimateStatus } from "@/lib/api/types"
import { StatusBadge } from "../status-badge"

// VIN-duplicate heads-up shown when the Vehicle step resolves a VIN that
// already has estimates in the user's stores. Same overlay primitives as
// ConfirmDialog, but the body is structured (match rows + conditional
// warning) rather than a string, so it lives as its own component.
//
// Non-blocking by design (req 2.5.1/2.5.2): every path out — backdrop, X,
// the alternative action — proceeds with the new estimate. We inform, never
// gate. And we do the deciding for them: the recommended action (reopen the
// existing job vs. start a fresh one) is chosen from the newest match's
// state and age, then rendered as the loud primary — one glance, one tap.

interface VinDuplicateDialogProps {
  open: boolean
  /** Existing estimates on this VIN, newest first, max 3. */
  matches: ApiEstimate[]
  /** "Open existing" — caller navigates to the most recent match. */
  onOpenExisting: () => void
  /** Dismiss and proceed with the new estimate. */
  onContinue: () => void
}

function vehicleLabel(e: ApiEstimate): string {
  return (
    [e.vehicle?.year, e.vehicle?.make, e.vehicle?.model]
      .filter(Boolean)
      .join(" ") || "Vehicle"
  )
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  })
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)
}

// Human recency so the advisor instantly reads "today" vs "months ago" —
// the distinction Sam called out: a scan from this morning is a memory
// lapse; one from six months back is almost certainly a new incident.
function relativeRecency(iso: string): string {
  const d = daysSince(iso)
  if (d <= 0) return "Today"
  if (d === 1) return "Yesterday"
  if (d < 7) return `${d} days ago`
  if (d < 14) return "Last week"
  if (d < 60) return `${Math.floor(d / 7)} weeks ago`
  if (d < 365) return `${Math.floor(d / 30)} months ago`
  const y = Math.floor(d / 365)
  return y === 1 ? "Over a year ago" : `${y} years ago`
}

function authorLabel(e: ApiEstimate): string | null {
  const a = e.createdBy
  if (!a?.firstName) return null
  return `by ${a.firstName}${a.lastName ? ` ${a.lastName[0]}.` : ""}`
}

// In-flight statuses — reopening the estimate is the obvious move.
// Completed/Declined are terminal, so a re-scan more likely means a fresh
// job and "start new" should lead instead.
const ACTIVE_STATUSES: EstimateStatus[] = [
  "Draft",
  "Pending",
  "Confirmed",
  "InProgress",
  "Scheduled",
]

export function VinDuplicateDialog({
  open,
  matches,
  onOpenExisting,
  onContinue,
}: VinDuplicateDialogProps) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  // Esc dismisses = continue; the dialog never blocks the advisor.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onContinue()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [open, onContinue])

  const declined = matches.find((m) => m.status === "Declined")
  const newest = matches[0]
  // Default the obvious action (doctrine: make the no-brainer the loud one).
  // Recent + still-open → reopening is almost always what they want; old or
  // finished → start fresh.
  const recommendOpen =
    !!newest &&
    ACTIVE_STATUSES.includes(newest.status) &&
    daysSince(newest.createdAt) <= 45

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center px-4 pb-4 sm:p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
        >
          <button
            type="button"
            aria-label="Continue with new estimate"
            onClick={onContinue}
            className="absolute inset-0 bg-zinc-900/55 backdrop-blur-[2px]"
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="vin-duplicate-dialog-title"
            initial={{ y: 24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 16, opacity: 0 }}
            transition={{ type: "spring", stiffness: 320, damping: 32 }}
            className="relative z-10 w-full sm:max-w-md bg-white rounded-3xl shadow-2xl shadow-zinc-900/30"
          >
            <div
              className="px-5 py-5 sm:px-6 sm:py-6"
              style={{ paddingBottom: "max(env(safe-area-inset-bottom), 20px)" }}
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h3
                  id="vin-duplicate-dialog-title"
                  className="text-[18px] font-bold text-zinc-900 tracking-tight leading-tight"
                >
                  This vehicle has an estimate on file
                </h3>
                <button
                  type="button"
                  aria-label="Continue with new estimate"
                  onClick={onContinue}
                  className="size-8 rounded-lg hover:bg-zinc-100 grid place-items-center text-zinc-500"
                >
                  <X className="size-4" aria-hidden />
                </button>
              </div>

              <div className="space-y-2 mb-3">
                {matches.map((m) => {
                  const author = authorLabel(m)
                  return (
                    <div
                      key={m.id}
                      className="rounded-xl border border-zinc-200 bg-zinc-50/50 px-3 py-2.5 flex items-center justify-between gap-3"
                    >
                      <div className="min-w-0">
                        <p className="text-[13px] font-semibold text-zinc-900 leading-tight truncate">
                          {vehicleLabel(m)}
                        </p>
                        <p className="text-[11.5px] text-zinc-500 mt-0.5 leading-tight truncate">
                          {relativeRecency(m.createdAt)}
                          {author && (
                            <>
                              <span className="text-zinc-300"> · </span>
                              {author}
                            </>
                          )}
                        </p>
                      </div>
                      <StatusBadge status={m.status} className="shrink-0" />
                    </div>
                  )
                })}
              </div>

              {declined && (
                <div className="rounded-xl border border-amber-200 bg-amber-50/60 px-3.5 py-2.5 mb-3 flex items-start gap-2.5">
                  <AlertCircle
                    className="size-4 text-amber-600 shrink-0 mt-0.5"
                    aria-hidden
                  />
                  <p className="text-[12.5px] font-medium text-amber-900 leading-snug">
                    A previous estimate on this VIN was declined on{" "}
                    {shortDate(declined.updatedAt)} — adjust your approach
                    before re-contacting the customer.
                  </p>
                </div>
              )}

              <p className="text-[11.5px] text-zinc-400 leading-snug mb-5">
                {recommendOpen
                  ? "Opening the existing one keeps it as a single job — a duplicate scan won’t earn the per-scan payout."
                  : "Starting new won’t touch the earlier estimate — it stays on file as its own record."}
              </p>

              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                <button
                  type="button"
                  onClick={recommendOpen ? onContinue : onOpenExisting}
                  className="h-11 px-4 rounded-xl border border-zinc-200 bg-white hover:bg-zinc-50 text-[13.5px] font-semibold text-zinc-800 transition-colors duration-150"
                >
                  {recommendOpen ? "Start new estimate" : "Open existing"}
                </button>
                <button
                  type="button"
                  onClick={recommendOpen ? onOpenExisting : onContinue}
                  className="h-11 px-4 rounded-xl bg-zinc-900 hover:bg-black text-white text-[13.5px] font-semibold transition-colors duration-150"
                >
                  {recommendOpen ? "Open existing" : "Start new estimate"}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
